import { Info, Loader2 } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { useUserInfo } from "layouts/user-info/_context/UserinfoContext";
import { FormInput } from "components/fields/FormInput";
import { handleAxiosAndCaughtError } from "utils";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "react-i18next";

const MainPage = () => {
  const { t } = useTranslation();
  const {
    userInfo,
    isLoading: contextLoading,
    error,
    editUser,
  } = useUserInfo();
  const { hasPermission } = useAuthContext();

  const permissions = {
    canUpdateUser: hasPermission(PERMISSIONS.UPDATE_USER),
  };

  // Memoize initial user data
  const initialUserData = useMemo(() => {
    return {
      name: userInfo?.name || "",
      image: null as File | null,
      imageUrl: userInfo?.image || "",
      email: userInfo?.email || "",
    };
  }, [userInfo]);

  const [formData, setFormData] = useState<{
    name: string;
    image: File | null;
    imageUrl: string;
    email: string;
  }>(initialUserData);

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    image?: string;
    email?: string;
  }>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");

  // Update formData when userInfo changes
  useEffect(() => {
    setFormData(initialUserData);
    setPreviewUrl("");
  }, [initialUserData]);

  // Preview uploaded image
  useEffect(() => {
    if (formData.image) {
      const url = URL.createObjectURL(formData.image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl("");
    }
  }, [formData.image]);

  // Check if form has changes
  useEffect(() => {
    const dataChanged =
      formData.name !== initialUserData.name ||
      formData.email !== initialUserData.email ||
      formData.image !== null ||
      formData.imageUrl !== initialUserData.imageUrl;
    setHasChanges(dataChanged);
  }, [formData, initialUserData]);

  // Validate form
  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("users.mainPage.nameRequired");
    }
    if (!formData.email.trim()) {
      newErrors.email = t("users.mainPage.emailRequired");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = t("users.mainPage.emailInvalid");
    }
    // image is optional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes for text fields
  const handleInputChange = (field: "name" | "email", value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      image: file || null,
      imageUrl: file ? "" : prev.imageUrl,
    }));
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imageUrl: "",
    }));
    setPreviewUrl("");
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // If you need to send a file, use FormData
      let payload: any = {
        name: formData.name,
        email: formData.email,
      };
      if (formData.image) {
        const fd = new FormData();
        fd.append("name", formData.name);
        fd.append("email", formData.email);
        fd.append("image", formData.image);
        payload = fd;
      } else if (formData.imageUrl) {
        payload.image = formData.imageUrl;
      }
      await editUser(payload);
      setHasChanges(false);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if save button should be disabled
  const isSaveDisabled =
    !hasChanges ||
    !formData.name.trim() ||
    !formData.email.trim() ||
    isLoading ||
    contextLoading ||
    !permissions.canUpdateUser;

  return (
    <>
      <div className="space-y-6">
        {/* Name Field */}
        <FormInput
          label={t("users.mainPage.nameLabel")}
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder={t("users.mainPage.namePlaceholder")}
          required
          error={errors.name}
          disabled={isLoading || contextLoading || !permissions.canUpdateUser}
        />

        {/* Image/Icon Field (File Upload) */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            {t("users.mainPage.iconLabel")}
          </label>
          <div className="flex items-center gap-4">
            <div className="relative h-16 w-16">
              <img
                src={previewUrl || formData.imageUrl || "/default-avatar.png"}
                alt={t("users.mainPage.profilePreview")}
                className="h-16 w-16 rounded-full border object-cover"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="image"
                className={`inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 ${
                  isLoading || contextLoading || !permissions.canUpdateUser
                    ? "cursor-not-allowed opacity-60"
                    : ""
                }`}
              >
                {t("users.mainPage.uploadPhoto")}
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={
                    isLoading || contextLoading || !permissions.canUpdateUser
                  }
                  className="hidden"
                />
              </label>
              {(formData.image || previewUrl || formData.imageUrl) && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-xs text-red-500 hover:underline"
                  disabled={
                    isLoading || contextLoading || !permissions.canUpdateUser
                  }
                >
                  {t("users.mainPage.removePhoto")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Email Field */}
        <FormInput
          label={t("users.mainPage.emailLabel")}
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder={t("users.mainPage.emailPlaceholder")}
          required
          error={errors.email}
          disabled={isLoading || contextLoading || !permissions.canUpdateUser}
        />
      </div>
      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaveDisabled || !permissions.canUpdateUser}
          className={`inline-flex min-w-[140px] items-center justify-center rounded-md px-6 py-3 text-sm font-medium transition-all duration-200 ${
            isSaveDisabled
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "bg-purple-600 text-white shadow-sm hover:bg-purple-700 hover:shadow-md active:bg-purple-800"
          }`}
        >
          {isLoading || contextLoading ? (
            <>
              <Loader2 className="animate-spin" />
              {t("users.mainPage.saving")}
            </>
          ) : (
            t("users.mainPage.saveChanges")
          )}
        </button>
      </div>
      {/* General error */}
      {error && (
        <div className="mt-4 flex items-center text-sm text-red-600">
          <Info className="mr-1" />
          {error}
        </div>
      )}
    </>
  );
};

export default MainPage;
